/**
 * Crafting - Main crafting menu component
 * 
 * Orchestrates the crafting interface with:
 * - Inventory sidebar (left)
 * - Crafting board (center)
 * - Recipe sidebar (right)
 * - Animations for placing, crafting, and removing ingredients
 */

import React, { useState, useRef, useCallback, useEffect, useMemo, MouseEvent } from "react";
import styles from "./crafting.module.css";
import DraggableSwinging from "../../components/draggableSwinging/draggableSwinging";
import ParticleRenderer from "../../components/particles/ParticleRenderer";
import { particleSystem } from "../../engine/particleSystem";
import { useCrafting, useGame } from "../../engine";
import { GAME_CONFIG } from "../../config/gameConfig";
import { Recipe } from "../../engine/types";

// Import utilities
import {
    countIngredientsOnBoard,
    canCraftWithBoard,
    getProductImage,
    formatTime,
    getRandomBenchPosition,
    getSidebarItemPosition,
    sortRecipes,
    ITEMS,
    RECIPES,
} from './craftingUtils';

// Import components
import {
    PlacedIngredient,
    CraftingOutput,
    RemovingIngredient,
    InventorySidebar,
    RecipeSidebar,
    PlacedIngredientData,
    TimedCraftingData,
    RemovingIngredientData,
    SelectedIngredient,
} from './components';

// Import hooks
import { useSwipeToClose } from './hooks';

interface CraftingProps {
    onClose?: () => void;
}

export default function Crafting({ onClose = () => {} }: CraftingProps): React.ReactElement {
    // ============================================
    // CONTEXT & STATE
    // ============================================
    
    const { inventory, craftingQueue, canCraft } = useCrafting();
    const { setCraftingDrag, addItem, removeItem, activeBoardCraft, setBoardCraft, clearBoardCraft } = useGame();

    // Selected ingredient being dragged
    const [selectedIngredient, setSelectedIngredient] = useState<SelectedIngredient | null>(null);
    
    // Ingredients placed on the crafting board
    const [ingredientsPlaced, setIngredientsPlaced] = useState<PlacedIngredientData[]>([]);
    
    // Crafting animation state
    // Phases: 'idle' -> 'converging' -> 'spinning' -> 'output' -> 'flyout' -> 'idle'
    const [craftingPhase, setCraftingPhase] = useState<string>('idle');
    const [craftingRecipe, setCraftingRecipe] = useState<Recipe | null>(null);
    const [craftingIngredientIds, setCraftingIngredientIds] = useState<number[]>([]);
    const [craftingCenter, setCraftingCenter] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [outputTargetPosition, setOutputTargetPosition] = useState<{ x: number; y: number } | null>(null);
    
    // Timed crafting state
    const [timedCrafting, setTimedCrafting] = useState<TimedCraftingData | null>(null);
    const [timedCraftingRemaining, setTimedCraftingRemaining] = useState(0);
    
    // Removing ingredient animations
    const [removingIngredients, setRemovingIngredients] = useState<RemovingIngredientData[]>([]);
    
    // Recipe pulse animation tracking
    const [pulsingRecipes, setPulsingRecipes] = useState<Set<string>>(new Set());
    const prevBoardCraftableRef = useRef<Set<string>>(new Set());
    
    // Close state
    const [isClosing, setIsClosing] = useState(false);

    // ============================================
    // REFS FOR CLEANUP
    // ============================================
    
    const selectedIngredientRef = useRef<SelectedIngredient | null>(null);
    const ingredientsPlacedRef = useRef<PlacedIngredientData[]>([]);
    const craftingIngredientIdsRef = useRef<number[]>([]);
    const componentMountedRef = useRef(true);
    const timedCraftingCompletedRef = useRef(false);
    const hasRestoredCraftingRef = useRef(false);
    
    // Refs for timeout cleanup (prevents memory leaks on unmount)
    const craftingTimeoutsRef = useRef<NodeJS.Timeout[]>([]);

    // Keep refs in sync with state
    useEffect(() => {
        selectedIngredientRef.current = selectedIngredient;
    }, [selectedIngredient]);
    
    useEffect(() => {
        ingredientsPlacedRef.current = ingredientsPlaced;
    }, [ingredientsPlaced]);
    
    useEffect(() => {
        craftingIngredientIdsRef.current = craftingIngredientIds;
    }, [craftingIngredientIds]);
    
    useEffect(() => {
        componentMountedRef.current = true;
        return () => {
            componentMountedRef.current = false;
            // Clean up all pending timeouts
            craftingTimeoutsRef.current.forEach(clearTimeout);
            craftingTimeoutsRef.current = [];
        };
    }, []);

    // ============================================
    // SWIPE TO CLOSE HOOK
    // ============================================
    
    const handleClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
        }, GAME_CONFIG.UI.ANIMATION_DURATION_MS);
    }, [onClose]);

    const {
        swipeOffset,
        isSwiping,
        animationComplete,
        handleSwipeStart,
    } = useSwipeToClose({
        onClose: handleClose,
        isClosing,
        disabled: selectedIngredient !== null,
        excludeSelectors: {
            leftSidebar: `.${styles.leftSidebar}`,
            rightSidebar: `.${styles.rightSidebar}`,
        },
    });

    // ============================================
    // CURSOR DRAG STATE SYNC
    // ============================================
    
    useEffect(() => {
        setCraftingDrag(selectedIngredient !== null);
        return () => setCraftingDrag(false);
    }, [selectedIngredient, setCraftingDrag]);

    // ============================================
    // CLEANUP ON UNMOUNT
    // ============================================
    
    useEffect(() => {
        return () => {
            // Return held ingredient
            if (selectedIngredientRef.current) {
                addItem(selectedIngredientRef.current.name, 1);
            }
            
            // Return placed ingredients except those being crafted
            const craftingIds = craftingIngredientIdsRef.current;
            ingredientsPlacedRef.current.forEach((ing, idx) => {
                if (!craftingIds.includes(idx)) {
                    addItem(ing.name, 1);
                }
            });
        };
    }, [addItem]);

    // ============================================
    // RESTORE TIMED CRAFTING STATE
    // ============================================
    
    useEffect(() => {
        // Only restore once - use ref to prevent repeated restoration
        if (hasRestoredCraftingRef.current) return;
        if (!activeBoardCraft || timedCrafting) return;
        
        hasRestoredCraftingRef.current = true;
        
        const recipe = RECIPES.find(r => r.id === activeBoardCraft.recipeId);
        if (recipe) {
            setIngredientsPlaced(activeBoardCraft.ingredients || []);
            setTimedCrafting({
                startedAt: activeBoardCraft.startedAt,
                duration: activeBoardCraft.duration,
                recipe,
                ingredientIds: activeBoardCraft.ingredientIds,
            });
            setCraftingRecipe(recipe);
            setCraftingIngredientIds(activeBoardCraft.ingredientIds);
            setCraftingPhase('spinning');
            
            requestAnimationFrame(() => {
                const pos = getSidebarItemPosition(recipe.outputs[0].item);
                setOutputTargetPosition(pos);
            });
        }
    }, [activeBoardCraft, timedCrafting]);

    // ============================================
    // TIMED CRAFTING TIMER
    // ============================================
    
    useEffect(() => {
        if (!timedCrafting) {
            timedCraftingCompletedRef.current = false;
            return;
        }
        
        const updateTimer = () => {
            const elapsed = Date.now() - timedCrafting.startedAt;
            const remaining = Math.max(0, timedCrafting.duration - elapsed);
            setTimedCraftingRemaining(remaining);
            
            if (remaining <= 0 && !timedCraftingCompletedRef.current) {
                timedCraftingCompletedRef.current = true;
                
                // Commit inventory changes immediately
                setIngredientsPlaced(prev => 
                    prev.filter((_, idx) => !timedCrafting.ingredientIds.includes(idx))
                );
                
                for (const out of timedCrafting.recipe.outputs) {
                    addItem(out.item, out.qty);
                }
                
                clearBoardCraft();
                setTimedCrafting(null);
                setCraftingPhase('output');
                
                // Cosmetic animations (tracked for cleanup)
                const flyoutTimeout = setTimeout(() => {
                    if (!componentMountedRef.current) return;
                    setCraftingPhase('flyout');
                }, GAME_CONFIG.CRAFTING.TIMED_OUTPUT_DELAY_MS);
                craftingTimeoutsRef.current.push(flyoutTimeout);
                
                const recipeForParticle = timedCrafting.recipe;
                const particleTimeout = setTimeout(() => {
                    if (!componentMountedRef.current) return;
                    const output = recipeForParticle.outputs[0];
                    const pos = getSidebarItemPosition(output.item);
                    particleSystem.spawnCraftingPlaceParticle(
                        pos.x,
                        pos.y - 20,
                        output.qty === 1 
                            ? `++${ITEMS[output.item]?.name || output.item};`
                            : `+${output.qty} ${ITEMS[output.item]?.name || output.item}`
                    );
                    setCraftingPhase('idle');
                    setCraftingRecipe(null);
                    setCraftingIngredientIds([]);
                }, GAME_CONFIG.CRAFTING.TIMED_PARTICLE_DELAY_MS);
                craftingTimeoutsRef.current.push(particleTimeout);
            }
        };
        
        updateTimer();
        const interval = setInterval(updateTimer, GAME_CONFIG.CRAFTING.TIMER_UPDATE_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [timedCrafting, addItem, clearBoardCraft]);

    // ============================================
    // BOARD COUNTS & RECIPE SORTING
    // ============================================
    
    const boardIngredientCounts = useMemo(() => {
        const available = ingredientsPlaced.filter((_, idx) => 
            !craftingIngredientIds.includes(idx)
        );
        return countIngredientsOnBoard(available);
    }, [ingredientsPlaced, craftingIngredientIds]);

    const sortedRecipes = useMemo(() => {
        return sortRecipes(RECIPES, boardIngredientCounts, inventory);
    }, [boardIngredientCounts, inventory]);

    // ============================================
    // RECIPE PULSE ANIMATION
    // ============================================
    
    useEffect(() => {
        const currentBoardCraftable = new Set(
            RECIPES.filter(r => canCraftWithBoard(r, boardIngredientCounts)).map(r => r.id)
        );
        
        const newlyEnabled = new Set<string>();
        currentBoardCraftable.forEach(id => {
            if (!prevBoardCraftableRef.current.has(id)) {
                newlyEnabled.add(id);
            }
        });
        
        if (newlyEnabled.size > 0) {
            setPulsingRecipes(newlyEnabled);
            const timeout = setTimeout(() => setPulsingRecipes(new Set()), GAME_CONFIG.CRAFTING.PULSE_DURATION_MS);
            return () => clearTimeout(timeout);
        }
        
        prevBoardCraftableRef.current = currentBoardCraftable;
    }, [boardIngredientCounts]);

    // ============================================
    // SPAWN INGREDIENTS ON BOARD
    // ============================================
    
    const spawnIngredientsOnBoard = useCallback((recipe: Recipe) => {
        const newIngredients: PlacedIngredientData[] = [];
        
        for (const input of recipe.inputs) {
            for (let i = 0; i < input.qty; i++) {
                if ((inventory[input.item] || 0) <= 0) continue;
                
                const targetPos = getRandomBenchPosition();
                const image = getProductImage(input.item);
                const sourcePos = getSidebarItemPosition(input.item);
                
                removeItem(input.item, 1);
                
                newIngredients.push({
                    name: input.item,
                    image,
                    x: targetPos.x,
                    y: targetPos.y,
                    currentX: sourcePos.x,
                    currentY: sourcePos.y,
                    spawnPhase: 'starting',
                    spawnId: Date.now() + Math.random(),
                });
            }
        }
        
        setIngredientsPlaced(prev => [...prev, ...newIngredients]);
        
        // Wait for DOM to commit the new elements before starting animation
        // Using setTimeout(0) instead of double RAF for clearer intent
        const animateTimeout = setTimeout(() => {
            if (!componentMountedRef.current) return;
            setIngredientsPlaced(prev => 
                prev.map(ing => {
                    if (ing.spawnPhase === 'starting') {
                        return { ...ing, currentX: ing.x, currentY: ing.y, spawnPhase: 'animating' };
                    }
                    return ing;
                })
            );
        }, 0);
        craftingTimeoutsRef.current.push(animateTimeout);
        
        const spawnCompleteTimeout = setTimeout(() => {
            if (!componentMountedRef.current) return;
            
            // First, spawn particles for animating ingredients (read current state via ref)
            const currentIngredients = ingredientsPlacedRef.current;
            currentIngredients.forEach(ing => {
                if (ing.spawnPhase === 'animating') {
                    particleSystem.spawnCraftingPlaceParticle(ing.x, ing.y, `--${ITEMS[ing.name]?.name || ing.name};`);
                }
            });
            
            // Then update state (separate from particle side effect)
            setIngredientsPlaced(prev => 
                prev.map(ing => {
                    if (ing.spawnPhase === 'animating') {
                        return { ...ing, spawnPhase: 'done', currentX: undefined, currentY: undefined };
                    }
                    return ing;
                })
            );
        }, GAME_CONFIG.CRAFTING.SPAWN_COMPLETE_MS);
        craftingTimeoutsRef.current.push(spawnCompleteTimeout);
    }, [inventory, removeItem]);

    // ============================================
    // HANDLE CRAFT
    // ============================================
    
    const handleCraft = useCallback((recipe: Recipe) => {
        if (craftingPhase !== 'idle' || timedCrafting) return;
        
        const canCraftFromBoard = canCraftWithBoard(recipe, boardIngredientCounts);
        const canCraftFromInventory = canCraft(recipe);
        
        if (!canCraftFromBoard && !canCraftFromInventory) return;
        
        if (canCraftFromBoard) {
            // Identify ingredients to use (skip ones already being crafted)
            const usedIndices: number[] = [];
            const tempCounts: Record<string, number> = {};
            
            ingredientsPlaced.forEach((ing, idx) => {
                // Skip ingredients that are already being crafted
                if (craftingIngredientIds.includes(idx)) return;
                
                const needed = recipe.inputs.find(input => input.item === ing.name);
                if (needed) {
                    const usedSoFar = tempCounts[ing.name] || 0;
                    if (usedSoFar < needed.qty) {
                        usedIndices.push(idx);
                        tempCounts[ing.name] = usedSoFar + 1;
                    }
                }
            });
            
            // Calculate center point of ingredients being crafted
            const craftingIngs = ingredientsPlaced.filter((_, idx) => usedIndices.includes(idx));
            const centerX = craftingIngs.reduce((sum, ing) => sum + ing.x, 0) / craftingIngs.length;
            const centerY = craftingIngs.reduce((sum, ing) => sum + ing.y, 0) / craftingIngs.length;
            
            setCraftingRecipe(recipe);
            setCraftingIngredientIds(usedIndices);
            setCraftingCenter({ x: centerX, y: centerY });
            setOutputTargetPosition(getSidebarItemPosition(recipe.outputs[0].item));
            setCraftingPhase('converging');
            
            const isTimedRecipe = recipe.time > 0;
            
            // Phase 2: Spinning (after converge animation)
            const spinTimeout = setTimeout(() => {
                if (!componentMountedRef.current) return;
                setCraftingPhase('spinning');
                
                if (isTimedRecipe) {
                    const craftData = {
                        startedAt: Date.now(),
                        duration: recipe.time * 1000,
                        recipe,
                        ingredientIds: usedIndices,
                    };
                    setTimedCrafting(craftData);
                    setTimedCraftingRemaining(recipe.time * 1000);
                    
                    const craftingIngredients = ingredientsPlaced.filter((_, idx) => 
                        usedIndices.includes(idx)
                    ).map(ing => ({
                        name: ing.name,
                        image: ing.image,
                        x: centerX,
                        y: centerY,
                    }));
                    
                    setBoardCraft({
                        startedAt: craftData.startedAt,
                        duration: craftData.duration,
                        recipeId: recipe.id,
                        ingredientIds: usedIndices,
                        ingredients: craftingIngredients,
                    });
                }
            }, GAME_CONFIG.CRAFTING.CONVERGE_TO_SPIN_MS);
            craftingTimeoutsRef.current.push(spinTimeout);
            
            // Instant recipe animation
            if (!isTimedRecipe) {
                for (const output of recipe.outputs) {
                    addItem(output.item, output.qty);
                }
                
                const outputTimeout = setTimeout(() => {
                    if (!componentMountedRef.current) return;
                    setCraftingPhase('output');
                }, GAME_CONFIG.CRAFTING.INSTANT_OUTPUT_DELAY_MS);
                craftingTimeoutsRef.current.push(outputTimeout);
                
                const flyoutTimeout = setTimeout(() => {
                    if (!componentMountedRef.current) return;
                    setCraftingPhase('flyout');
                }, GAME_CONFIG.CRAFTING.INSTANT_FLYOUT_DELAY_MS);
                craftingTimeoutsRef.current.push(flyoutTimeout);
                
                const resetTimeout = setTimeout(() => {
                    if (!componentMountedRef.current) return;
                    const output = recipe.outputs[0];
                    const pos = getSidebarItemPosition(output.item);
                    particleSystem.spawnCraftingPlaceParticle(
                        pos.x,
                        pos.y - 20,
                        output.qty === 1 
                            ? `++${ITEMS[output.item]?.name || output.item};`
                            : `+${output.qty} ${ITEMS[output.item]?.name || output.item}`
                    );
                    setIngredientsPlaced(prev => prev.filter((_, idx) => !usedIndices.includes(idx)));
                    setCraftingPhase('idle');
                    setCraftingRecipe(null);
                    setCraftingIngredientIds([]);
                }, GAME_CONFIG.CRAFTING.INSTANT_RESET_DELAY_MS);
                craftingTimeoutsRef.current.push(resetTimeout);
            }
        } else {
            spawnIngredientsOnBoard(recipe);
        }
    }, [canCraft, boardIngredientCounts, ingredientsPlaced, craftingIngredientIds, addItem, spawnIngredientsOnBoard, craftingPhase, timedCrafting, setBoardCraft]);

    // ============================================
    // INGREDIENT DROP HANDLER
    // ============================================
    
    const handleIngredientDrop = useCallback(({ x, y }: { x: number; y: number }) => {
        if (!selectedIngredient) return;
        
        // Check if drop is on the crafting bench
        const bench = document.getElementById('craftingBench');
        if (!bench) {
            // Bench not found, return item to inventory
            setSelectedIngredient(null);
            setCraftingDrag(false); // Ensure cursor reappears
            return;
        }
        
        const benchRect = bench.getBoundingClientRect();
        const isOnBench = (
            x >= benchRect.left &&
            x <= benchRect.right &&
            y >= benchRect.top &&
            y <= benchRect.bottom
        );
        
        if (!isOnBench) {
            // Dropped outside bench - return item to inventory (already there, just clear selection)
            setSelectedIngredient(null);
            setCraftingDrag(false); // Ensure cursor reappears
            return;
        }
        
        const ingredientName = selectedIngredient.name;
        
        removeItem(ingredientName, 1);
        particleSystem.spawnCraftingPlaceParticle(x, y, `--${ITEMS[ingredientName]?.name || ingredientName};`);
        
        setSelectedIngredient(null);
        setCraftingDrag(false); // Ensure cursor reappears
        setIngredientsPlaced(prev => [
            ...prev,
            { name: ingredientName, image: selectedIngredient.image, x, y }
        ]);
    }, [selectedIngredient, removeItem, setCraftingDrag]);

    // ============================================
    // REMOVE PLACED INGREDIENT
    // ============================================
    
    const removePlacedIngredient = useCallback((ingredient: PlacedIngredientData) => {
        addItem(ingredient.name, 1);
        
        setIngredientsPlaced(prev =>
            prev.filter(ing => ing.x !== ingredient.x || ing.y !== ingredient.y)
        );
        
        const targetPos = getSidebarItemPosition(ingredient.name);
        const removeId = `remove_${Date.now()}_${Math.random()}`;
        
        setRemovingIngredients(prev => [...prev, {
            id: removeId,
            ingredient: {
                name: ingredient.name,
                image: ingredient.image,
            },
            startX: ingredient.x,
            startY: ingredient.y,
            targetX: targetPos.x,
            targetY: targetPos.y,
            phase: 'starting',
        }]);
        
        requestAnimationFrame(() => {
            if (!componentMountedRef.current) return;
            setRemovingIngredients(prev => prev.map(r => 
                r.id === removeId ? { ...r, phase: 'flyout' as const } : r
            ));
        });
        
        setTimeout(() => {
            if (!componentMountedRef.current) return;
            particleSystem.spawnCraftingPlaceParticle(
                targetPos.x,
                targetPos.y - 20,
                `++${ITEMS[ingredient.name]?.name || ingredient.name};`
            );
            setRemovingIngredients(prev => prev.filter(r => r.id !== removeId));
        }, GAME_CONFIG.CRAFTING.REMOVE_FLYOUT_MS);
    }, [addItem]);

    const handlePlacedIngredientClick = useCallback((e: MouseEvent, ingredient: PlacedIngredientData) => {
        e.stopPropagation();
        removePlacedIngredient(ingredient);
    }, [removePlacedIngredient]);

    const stopPropagation = (e: MouseEvent) => e.stopPropagation();

    // ============================================
    // RENDER
    // ============================================
    
    return (
        <div 
            className={`${styles.craftingBlurBackground} ${isClosing ? styles.closing : ''}`}
            onTouchStart={animationComplete && !selectedIngredient ? handleSwipeStart : undefined}
        >
            {/* Currently dragged ingredient */}
            {selectedIngredient && (
                <DraggableSwinging
                    id="ingredient"
                    initialDragging
                    onDrop={handleIngredientDrop}
                    ropeLength={10}
                    throwable={false}
                    canGoOffScreen={true}
                >
                    <img draggable={false} src={selectedIngredient.image} alt={selectedIngredient.name} />
                </DraggableSwinging>
            )}

            {/* Placed ingredients on the crafting area */}
            {ingredientsPlaced.map((ingredient, index) => (
                <PlacedIngredient
                    key={ingredient.spawnId || `${ingredient.name}-${index}`}
                    ingredient={ingredient}
                    isBeingCrafted={craftingIngredientIds.includes(index)}
                    craftingPhase={craftingPhase}
                    craftingCenter={craftingCenter}
                    timedCrafting={timedCrafting}
                    isClosing={isClosing}
                    isEntering={!animationComplete}
                    onClick={handlePlacedIngredientClick}
                />
            ))}
            
            {/* Timed crafting timer display */}
            {timedCrafting && craftingPhase === 'spinning' && (
                <p className={styles.craftingTimer}>
                    {formatTime(timedCraftingRemaining)}
                </p>
            )}
            
            {/* Crafting output animation */}
            <CraftingOutput
                recipe={craftingRecipe}
                craftingPhase={craftingPhase}
                craftingCenter={craftingCenter}
                outputTargetPosition={outputTargetPosition}
            />
            
            {/* Removing ingredient flyout animations */}
            {removingIngredients.map(removing => (
                <RemovingIngredient key={removing.id} removing={removing} />
            ))}

            {/* Particle system renderer */}
            <ParticleRenderer />

            {/* Crafting bench */}
            <div 
                className={`${styles.benchTop} ${isClosing ? styles.closing : ''} ${animationComplete ? styles.animationDone : ''}`} 
                id="craftingBench"
                onClick={stopPropagation}
                onKeyDown={(e) => e.stopPropagation()}
                onTouchStart={animationComplete ? handleSwipeStart : undefined}
                role="region"
                aria-label="Crafting bench"
                style={animationComplete ? {
                    transform: `translate(-50%, -50%) translateY(${swipeOffset}px)`,
                    transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
                } : undefined}
            >
                <img
                    draggable={false}
                    className={styles.craftingInstructions}
                    src="./images/crafting/instruction.svg"
                    alt="Crafting instructions"
                />
            </div>

            {/* Inventory sidebar (left) */}
            <InventorySidebar
                inventory={inventory}
                isClosing={isClosing}
                onSelectIngredient={setSelectedIngredient}
                onStopPropagation={stopPropagation}
            />

            {/* Recipe sidebar (right) */}
            <RecipeSidebar
                sortedRecipes={sortedRecipes}
                boardIngredientCounts={boardIngredientCounts}
                inventory={inventory}
                pulsingRecipes={pulsingRecipes}
                isClosing={isClosing}
                onCraft={handleCraft}
                onStopPropagation={stopPropagation}
            />

            {/* Crafting queue display */}
            {craftingQueue.length > 0 && (
                <div className={`${styles.craftingQueue} ${isClosing ? styles.closing : ''}`}>
                    <p style={{ color: 'black', margin: '5px 0', fontWeight: 'bold' }}>Crafting:</p>
                    {craftingQueue.map(craft => {
                        const recipe = RECIPES.find(r => r.id === craft.recipeId);
                        const remainingTime = Math.max(0, Math.ceil((craft.completesAt - Date.now()) / 1000));
                        return (
                            <div key={craft.id} style={{ color: 'black', fontSize: 12 }}>
                                {recipe?.name || craft.recipeId}: {remainingTime}s
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Close hint */}
            <p className={`${styles.closeHint} ${isClosing ? styles.closing : ''}`}>
                scroll or swipe down to close
            </p>
        </div>
    );
}

